/**
 * Builds the "Showing data for: …" summary shown under the Ticketing Analytics filters.
 *
 * This module is intentionally UI-agnostic: it contains every formatting and business
 * rule for the summary and exposes a single entry point (`getTicketingSummary`).
 * Consuming components render the returned strings verbatim.
 *
 * Extension points:
 *  - `TIME_PERIOD_FORMATTERS` — add a new time period by adding one entry.
 *  - `SCOPE_RULES` — add a new ticket scope ("Open tickets", "Critical tickets", …)
 *    by adding one rule. Neither requires touching the UI.
 */

import { formatShortDate } from '../../../../utils/dateFormatter';
import { deriveFiscalQuarter, deriveWeekInYear } from '../../util/timePeriodUtils';
import type {
  TicketingAnalyticsCustomDateRange,
  TicketingAnalyticsSelectedFilters,
  TimePeriod,
} from '../../components/types';

/** Current fiscal context, as stored in Redux (`appData.periodWeek.data`). */
export interface TicketingSummaryPeriodContext {
  period?: number;
  week?: number;
  year?: number;
  quarter?: number;
  date?: string;
}

export interface TicketingSummary {
  /** Ticket scope portion, e.g. "All tickets". */
  scope: string;
  /** Time period portion, e.g. "Period to date (P6 2026)". */
  timePeriod: string;
  /** Filter details, e.g. "Region: East · Market: Mid-Atlantic · Location: Atlanta GA". */
  filterDetails?: string;
  /** Pre-composed summary, e.g. "All tickets · Period to date (P6 2026)". */
  text: string;
}

export interface GetTicketingSummaryArgs {
  appliedFilters: TicketingAnalyticsSelectedFilters;
  appliedTimePeriod: TimePeriod;
  appliedCustomDateRange: TicketingAnalyticsCustomDateRange;
  periodWeekContext?: TicketingSummaryPeriodContext;
}

/** Separator between the scope and the time period. */
const SUMMARY_SEPARATOR = ' · ';

/** En-dash separator for custom date ranges. */
const DATE_RANGE_SEPARATOR = ' – ';

const DEFAULT_SCOPE_LABEL = 'All tickets';
const EMPTY_CUSTOM_RANGE_LABEL = 'Custom date range';
const UNKNOWN_PERIOD_LABEL = 'Selected period';

/** Resolved values every time-period formatter can rely on. */
interface TimePeriodFormatterContext {
  /** Fiscal year of the current selection. */
  year: number;
  /** Fiscal period (1–13), when known. */
  period?: number;
  /** Absolute week-in-year (1–52), when known. */
  weekInYear?: number;
  /** Fiscal quarter (1–4), when known. */
  quarter?: number;
  /** Calendar date backing a "Day" selection (`YYYY-MM-DD`). */
  date: string;
  /**
   * True when the selection matches the live fiscal context, which decides
   * between the "to date" form ("Period to date (P6 2026)") and the plain
   * historical form ("P4 2026").
   */
  isCurrent: boolean;
  customDateRange: TicketingAnalyticsCustomDateRange;
}

const getTodayIso = (): string => {
  const today = new Date();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

/**
 * Format a custom range. Handles partially selected ranges gracefully and never
 * falls back to another time period, since the user explicitly chose a range.
 */
const formatCustomRange = (range: TicketingAnalyticsCustomDateRange): string => {
  const start = formatShortDate(range.startDate);
  const end = formatShortDate(range.endDate);

  if (start && end) {
    return `${start}${DATE_RANGE_SEPARATOR}${end}`;
  }

  return start || end || EMPTY_CUSTOM_RANGE_LABEL;
};

/**
 * Compose a fiscal label, choosing between the "to date" and historical forms.
 * `unit` is the human label ("Period"), `value` the short form ("P6 2026").
 */
const formatFiscalLabel = (unit: string, value: string, isCurrent: boolean): string =>
  isCurrent ? `${unit} to date (${value})` : value;

/**
 * One formatter per supported time period. Adding a period means adding an entry
 * here — no other file changes.
 */
const TIME_PERIOD_FORMATTERS: Record<TimePeriod, (context: TimePeriodFormatterContext) => string> = {
  day: ({ date }) => formatShortDate(date) || UNKNOWN_PERIOD_LABEL,

  week: ({ weekInYear, year }) =>
    weekInYear ? `Week ${weekInYear} (${year})` : `Week (${year})`,

  period: ({ period, year, isCurrent }) =>
    period
      ? formatFiscalLabel('Period', `P${period} ${year}`, isCurrent)
      : formatFiscalLabel('Period', `${year}`, isCurrent),

  quarter: ({ quarter, year, isCurrent }) =>
    quarter
      ? formatFiscalLabel('Quarter', `Q${quarter} ${year}`, isCurrent)
      : formatFiscalLabel('Quarter', `${year}`, isCurrent),

  year: ({ year, isCurrent }) => formatFiscalLabel('Year', `${year}`, isCurrent),

  custom: ({ customDateRange }) => formatCustomRange(customDateRange),
};

/**
 * Filters that describe *which tickets* are in scope (as opposed to where they
 * came from). Used to decide whether the scope is still "All tickets".
 */
const TICKET_SCOPE_FILTER_KEYS: (keyof TicketingAnalyticsSelectedFilters)[] = [
  'statuses',
  'urgencies',
  'categories',
  'subCategories',
  'tertiaries',
  'resolutionPaths',
  'assignedTo',
];

const hasTicketScopeFilters = (filters: TicketingAnalyticsSelectedFilters): boolean =>
  TICKET_SCOPE_FILTER_KEYS.some((key) => (filters[key]?.length ?? 0) > 0);

interface ScopeRule {
  matches: (filters: TicketingAnalyticsSelectedFilters) => boolean;
  label: (filters: TicketingAnalyticsSelectedFilters) => string;
}

/**
 * Ordered scope rules — the first match wins. Richer scopes ("Open tickets",
 * "Critical tickets", "Open + Critical tickets", "Assigned to Me") can be added
 * above the terminal rule without changing the public API or the UI.
 */
const SCOPE_RULES: ScopeRule[] = [
  {
    // No ticket-specific filter applied — everything is in scope.
    matches: (filters) => !hasTicketScopeFilters(filters),
    label: () => DEFAULT_SCOPE_LABEL,
  },
  {
    // Terminal rule: keeps the contract total until specific scopes are defined.
    matches: () => true,
    label: () => DEFAULT_SCOPE_LABEL,
  },
];

const buildScopeLabel = (filters: TicketingAnalyticsSelectedFilters): string => {
  const rule = SCOPE_RULES.find((candidate) => candidate.matches(filters));
  return rule ? rule.label(filters) : DEFAULT_SCOPE_LABEL;
};

/**
 * Build a detailed filter summary string from all applied filters.
 * Only includes filters that have been explicitly selected (not "All").
 * Example: "Region: East · Market: Mid-Atlantic · Location: Atlanta GA, Charlotte NC · Category: Access Request · Sub-Category: Vision Pick (WES)"
 */
const buildFilterDetails = (filters: TicketingAnalyticsSelectedFilters): string => {
  const parts: string[] = [];

  // Helper: check if a filter should be shown
  // - Must have items
  // - Must not contain "all" sentinel
  // - Must have at least one meaningful selection
  const shouldShowFilter = (items: any[] | undefined): boolean => {
    if (!items || items.length === 0) return false;
    // If "All" is in the selection, skip this filter
    if (items.some((item) => String(item.id).toLowerCase() === 'all')) return false;
    return true;
  };

  // Regions
  if (shouldShowFilter(filters.regions)) {
    const regionLabels = filters.regions
      .map((r) => r.displayText)
      .join(', ');
    parts.push(`Region: ${regionLabels}`);
  }

  // Markets
  if (shouldShowFilter(filters.markets)) {
    const marketLabels = filters.markets
      .map((m) => m.displayText)
      .join(', ');
    parts.push(`Market: ${marketLabels}`);
  }

  // Locations
  if (shouldShowFilter(filters.locations)) {
    const locationLabels = filters.locations
      .map((l) => l.displayText)
      .join(', ');
    parts.push(`Location: ${locationLabels}`);
  }

  // Categories
  if (shouldShowFilter(filters.categories)) {
    const categoryLabels = filters.categories
      .map((c) => c.displayText)
      .join(', ');
    parts.push(`Category: ${categoryLabels}`);
  }

  // Sub-Categories
  if (shouldShowFilter(filters.subCategories)) {
    const subCategoryLabels = filters.subCategories
      .map((s) => s.displayText)
      .join(', ');
    parts.push(`Sub-Category: ${subCategoryLabels}`);
  }

  // Tertiary
  if (shouldShowFilter(filters.tertiaries)) {
    const tertiaryLabels = filters.tertiaries
      .map((t) => t.displayText)
      .join(', ');
    parts.push(`Tertiary: ${tertiaryLabels}`);
  }

  // Statuses
  if (shouldShowFilter(filters.statuses)) {
    const statusLabels = filters.statuses
      .map((s) => s.displayText)
      .join(', ');
    parts.push(`Status: ${statusLabels}`);
  }

  // Resolution Paths
  if (shouldShowFilter(filters.resolutionPaths)) {
    const rpLabels = filters.resolutionPaths
      .map((r) => r.displayText)
      .join(', ');
    parts.push(`Resolution Path: ${rpLabels}`);
  }

  // Urgencies
  if (shouldShowFilter(filters.urgencies)) {
    const urgencyLabels = filters.urgencies
      .map((u) => u.displayText)
      .join(', ');
    parts.push(`Urgency: ${urgencyLabels}`);
  }

  // Assigned To
  if (shouldShowFilter(filters.assignedTo)) {
    const assignedLabels = filters.assignedTo
      .map((a) => a.displayText)
      .join(', ');
    parts.push(`Assigned: ${assignedLabels}`);
  }

  return parts.length > 0 ? parts.join(SUMMARY_SEPARATOR) : '';
};

const buildFormatterContext = (
  appliedCustomDateRange: TicketingAnalyticsCustomDateRange,
  periodWeekContext?: TicketingSummaryPeriodContext,
): TimePeriodFormatterContext => {
  const currentYear = new Date().getFullYear();
  const year = periodWeekContext?.year || currentYear;
  const period = periodWeekContext?.period || undefined;
  const week = periodWeekContext?.week || undefined;

  return {
    year,
    period,
    weekInYear: deriveWeekInYear(period, week),
    quarter: periodWeekContext?.quarter ?? deriveFiscalQuarter(period),
    date: periodWeekContext?.date ?? getTodayIso(),
    // Selections are sourced from the live fiscal context today, so they always
    // represent the in-progress period. Once historical selection is supported,
    // this comparison automatically yields the plain form (e.g. "P4 2026").
    isCurrent: !periodWeekContext?.year || periodWeekContext.year === currentYear,
    customDateRange: appliedCustomDateRange,
  };
};

/**
 * Build the ticket scope and time-period descriptions for the applied filters.
 * Always call this with *applied* (fetched) filter state so the summary describes
 * the data currently on screen rather than an unsubmitted draft selection.
 */
export const getTicketingSummary = ({
  appliedFilters,
  appliedTimePeriod,
  appliedCustomDateRange,
  periodWeekContext,
}: GetTicketingSummaryArgs): TicketingSummary => {
  const context = buildFormatterContext(appliedCustomDateRange, periodWeekContext);
  const formatter = TIME_PERIOD_FORMATTERS[appliedTimePeriod];

  const scope = buildScopeLabel(appliedFilters);
  const timePeriod = formatter ? formatter(context) : UNKNOWN_PERIOD_LABEL;
  const filterDetails = buildFilterDetails(appliedFilters);

  // Build the text: if there are filter details, show them first, then time period
  // Otherwise just show scope and time period as before
  let text: string;
  if (filterDetails) {
    text = `${filterDetails}${SUMMARY_SEPARATOR}${timePeriod}`;
  } else {
    text = `${scope}${SUMMARY_SEPARATOR}${timePeriod}`;
  }

  return {
    scope,
    timePeriod,
    filterDetails,
    text,
  };
};
